import React, {Component, DragEvent} from 'react';
import {ConnectDragSource, DragSource, DragSourceConnector, DragSourceSpec} from 'react-dnd';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {addDownloadToDragEvent} from '../../../../../helpers/dragAndDropHelpers';
import {
  AttachmentModel,
  AttachmentStatusesEnum,
  UploadedAttachmentModel,
} from '../../../../../models/api/attachmentModel';
import {
  addNativeAttachmentsToDragEvent,
  DraggedAttachmentItemTypesEnum,
} from '../../../../common/attachments/attachmentsDragAndDrop';

/*
 * Constants.
 */

export const AttachmentDragType = 'ATTACHMENT';

/*
 * Props.
 */

interface AttachmentDragSourceProps {
  className?: string;
  attachment: AttachmentModel;
  connectDragSource: ConnectDragSource;
}

export type AttachmentDragItem = UploadedAttachmentModel;

/*
 * Component.
 */

class AttachmentDragSourceComponent extends Component<AttachmentDragSourceProps> {
  private readonly onDragStart: HandlerOf<DragEvent> = (e) => {
    const {attachment} = this.props;
    if (attachment.status !== AttachmentStatusesEnum.UPLOADED) {
      return;
    }

    addNativeAttachmentsToDragEvent(e, {
      type: DraggedAttachmentItemTypesEnum.UPLOADED_ATTACHMENTS,
      attachments: [attachment],
    });

    // Setting effectAllowed = 'copy' is needed in addition to the
    // dropEffect: 'copy' drag source option, since that only has the
    // desired effect when dragging over something in the page, not when
    // communicating with the host OS.
    e.dataTransfer.effectAllowed = 'copy';
    addDownloadToDragEvent(e, {...getAttachmentDownloadOptions(attachment), dropEffect: 'copy'});
  };

  /*
   * Render.
   */

  render() {
    const {attachment, connectDragSource, children} = this.props;
    if (attachment.status !== AttachmentStatusesEnum.UPLOADED) {
      return children;
    }

    return (
      <>
        {connectDragSource(
          <div className={this.props.className} onDragStart={this.onDragStart}>
            {children}
          </div>,
          // This is required for the in-app dnd to work
          {
            dropEffect: 'copy',
          },
        )}
      </>
    );
  }
}

function getAttachmentDownloadOptions(attachment: UploadedAttachmentModel) {
  const {name, url} = attachment;
  return {
    name: name || 'attachment.dat',
    url: url.startsWith('data:') ? url : `${location.origin}${url}`,
  };
}

/*
 * DnD.
 */

const spec: DragSourceSpec<AttachmentDragSourceProps, AttachmentDragItem> = {
  canDrag({attachment}) {
    return attachment.status === AttachmentStatusesEnum.UPLOADED;
  },
  beginDrag({attachment}) {
    if (attachment.status !== AttachmentStatusesEnum.UPLOADED) {
      throw new Error('This attachment cannot be dragged.');
    }

    return attachment;
  },
};

function collect(connect: DragSourceConnector) {
  return {
    connectDragSource: connect.dragSource(),
  };
}

export const AttachmentDragSource = DragSource(
  AttachmentDragType,
  spec,
  collect,
)(AttachmentDragSourceComponent);
